import { HttpClient } from "@angular/common/http";
import { EventEmitter, Inject, Injectable, Output } from "@angular/core";
import { BehaviorSubject, catchError, firstValueFrom, last, lastValueFrom, map, Observable, of, tap } from "rxjs";
import { TaskAgenda } from "../api/tasks/my-tasks/my-tasks.state";
import { isNullOrUndefined, isNullOrWhitespace } from "../common/functions";
import { BuildConfigService } from "./build-config.service";
import { Honeycomb } from "./honeycomb-api/honeycomb-api";
import FilterInit, { TaskSearchProperties } from "./tasks-filter";
import { AgendaLoading, TaskPageResult } from "./tasks-page-resutl";

@Injectable({
    providedIn: 'root'
})

export class TasksService {

    public initialized = false;

    public loading: BehaviorSubject<AgendaLoading> = new BehaviorSubject({ agendaKey: null, loading: false });
    
    private callIndex: number = 0; // for synchronization possible parallel calls

    public jobIdChangeEvent: number = null;

    @Output()
    public data: EventEmitter<TaskPageResult> = new EventEmitter<TaskPageResult>();

    @Output()
    public total: EventEmitter<number> = new EventEmitter<number>();

    @Output()
    public loadingData: EventEmitter<AgendaLoading> = new EventEmitter<AgendaLoading>();

    @Output()
    public forcePagerUpdate: EventEmitter<void> = new EventEmitter();

    public totalStatic: number = 0;

    private operationsAll: Array<Honeycomb.Tenant.Tasker.IService.Model.Operation> = null;

    private taskStatuses: Array<Honeycomb.Tenant.Tasker.IService.Model.TaskStatus> = null;

    private lastAgenda: TaskAgenda = TaskAgenda.Dashboard;

    public pageSize(agendaKey?: string): number {

        console.log('pageSize', agendaKey, this.taskSearchProperties.pageSizeMap.get(agendaKey));

        if (!isNullOrWhitespace(agendaKey)) {
            return this.taskSearchProperties.pageSizeMap.get(agendaKey);
        }
        return this.taskSearchProperties.listFilter.pageSize || 10;
    }

    public pageIndex(agendaKey?: string): number {

        console.log('pageIndex', agendaKey, this.taskSearchProperties.pageIndexMap.get(agendaKey));

        if (!isNullOrWhitespace(agendaKey)) {
            return this.taskSearchProperties.pageIndexMap.get(agendaKey);
        }
        return this.taskSearchProperties.listFilter.pageIndex || 10;
    }

    public operationsGet(agendaKey: string): Array<number> {

        console.log('operations', agendaKey, this.taskSearchProperties.operationsMap.get(agendaKey));
        return this.taskSearchProperties.operationsMap.get(agendaKey);
    }

    private taskSearchProperties: TaskSearchProperties;

    constructor(
        private http: HttpClient,
        @Inject('TaskController')
        private taskController: Honeycomb.Tenant.Tasker.IService.Controller.TaskController,
        @Inject('OperationController') private operationController: Honeycomb.Tenant.Tasker.IService.Controller.OperationController,
        @Inject('TaskStatusController') private taskStatusController: Honeycomb.Tenant.Tasker.IService.Controller.TaskStatusController,
        @Inject('ConfigController') private configController: Honeycomb.Tenant.Admin.IService.NamedConfigurationsController,
        private buildConfig: BuildConfigService
        ) {
        this.taskSearchProperties = FilterInit();
    }

    private async loadOperations() {
        this.operationsAll = await firstValueFrom(this.operationController.ActiveList(null));
        return this.operationsAll;
    }

    public async setDefaultAgendaFilter(reset?: boolean, agenda?: TaskAgenda, jobID?: number) {
        if (isNullOrUndefined(agenda) || reset || this.lastAgenda !== agenda) {
            this.taskSearchProperties.listFilter = new Honeycomb.Tenant.Tasker.IService.TaskListFilter();
        }
        if (!isNullOrUndefined(agenda) && this.lastAgenda !== agenda && agenda !== TaskAgenda.Calendar) {
            await this.setDefaultAttributesByAgenda(agenda);
            await this.setDefaultOperationsByAgenda(agenda);
        }
        if (!!jobID && this.lastAgenda !== agenda && agenda !== TaskAgenda.Calendar) {
            await this.setDefaultTaskStatuses(jobID);
        }
        if (reset) {
            var sortResponse = await lastValueFrom(this.configController.GetByNames(['tasker.tasks.additional-settings']));
            var sortConfig = sortResponse?.['tasker.tasks.additional-settings']?.ordering;
            this.setSort(sortConfig?.sort || "taskID", sortConfig?.direction || "desc", sortConfig?.attribute || false);
        }
        if (agenda == TaskAgenda.Calendar) {
            this.setSort('dateFrom', 'asc');
        }
    }

    public async setDefaultOperationsByAgenda(agenda: TaskAgenda) {

        let agendasPrefixes = ['tasks-main-list-', 'tasks-main-table-'];
        let agendaKeys = agendasPrefixes.map(p => p + agenda);

        if (!this.operationsAll) {
            await this.loadOperations();
        }
        if (agenda !== TaskAgenda.Repairs) {
            var preselectedOperations = this.operationsAll.filter(p => p.taskType !== Honeycomb.Common.Enums.TaskType.repairRequest);
            agendaKeys.forEach(a => { 
                this.taskSearchProperties.operationsMap.set(a, preselectedOperations.map(o => o.operationID));
            })
        }
        else {
           var  preselectedOperations = this.operationsAll.filter(p => p.taskType === Honeycomb.Common.Enums.TaskType.repairRequest);
            agendaKeys.forEach(a => { 
                this.taskSearchProperties.operationsMap.set(a, preselectedOperations.map(o => o.operationID));
            })
        }
    }

    public async setDefaultAttributesByAgenda(agenda: TaskAgenda) {

        let filter = this.taskSearchProperties.listFilter
        if (agenda === TaskAgenda.Repairs) {
            filter.includeAttributes = this.buildConfig.taskList.repair.attributes;
        } else {
            filter.includeAttributes = this.buildConfig.taskList.default.attributes;
        }
    }

    public async setDefaultTaskStatuses(selectedJobID: number) {
        this.taskSearchProperties.listFilter.jobID = selectedJobID;
        this.taskStatuses = await firstValueFrom(this.taskStatusController.List(null, selectedJobID));
        let filter = this.taskSearchProperties.listFilter;
        filter.taskStatusIDs = this.taskStatuses.filter(s => s.defaultFilter).map(a => a.taskStatusID);
    }

    public async loadPage(agendaKey?: string): Promise<Observable<TaskPageResult>> {

        console.log('loadPage', agendaKey);

        this.lastAgenda = this.taskSearchProperties.agenda;

        if (!this.taskSearchProperties.listFilter.taskRelationAdditional) {
            this.taskSearchProperties.listFilter.taskRelationAdditional = [];
        }

        if (this.jobIdChangeEvent)
        {
            this.taskSearchProperties.listFilter.jobID = this.jobIdChangeEvent;
        }

        let operationIDs = this.taskSearchProperties.operationsMap.get(agendaKey);
        if (this.taskSearchProperties.listFilter.operations && this.taskSearchProperties.listFilter.operations.length > 0) {
            operationIDs = this.taskSearchProperties.listFilter.operations;
        }

        const filter = {
            userID: -1,
            jobID: this.taskSearchProperties.listFilter.jobID,
            pageIndex: this.taskSearchProperties.pageIndexMap.get(agendaKey),
            pageSize: this.taskSearchProperties.pageSizeMap.get(agendaKey),
            filter: {
                fulltext: this.taskSearchProperties.listFilter.fulltext,
                assignedUsers: this.taskSearchProperties.listFilter.taskRelationAdditional.filter(r => r.taskRelation === Honeycomb.Common.Enums.TaskRelation.assignee).map(r => r.userID), // TODO
                assignerUsers: this.taskSearchProperties.listFilter.taskRelationAdditional.filter(r => r.taskRelation === Honeycomb.Common.Enums.TaskRelation.assigner).map(r => r.userID), // TODO
                creatorUsers: this.taskSearchProperties.listFilter.taskRelationAdditional.filter(r => r.taskRelation === Honeycomb.Common.Enums.TaskRelation.creator).map(r => r.userID),  // TODO
                name: null,
                description: null,
                mainFilter: this.taskSearchProperties.listFilter.mainFilter,
                locationIDs: this.taskSearchProperties.listFilter.locations,
                operationIDs: operationIDs,
                priorities: this.taskSearchProperties.listFilter.priorities,
                taskStatusIDs: this.taskSearchProperties.listFilter.taskStatusIDs,
                dateFrom: this.taskSearchProperties.listFilter.dateFrom,
                dateTo: this.taskSearchProperties.listFilter.dateTo,
                taskRead: this.taskSearchProperties.listFilter.taskRead === Honeycomb.Common.Enums.TaskRead.read ? true : 
                                this.taskSearchProperties.listFilter.taskRead === Honeycomb.Common.Enums.TaskRead.notRead ? false : null,

            } as Honeycomb.Tenant.Tasker.IService.Model.TaskPageFilter,
            sort: this.taskSearchProperties.sort,
        } as Honeycomb.Tenant.Tasker.IService.Model.TaskPage;

        console.log('PageRequest - filter', filter, agendaKey);

        let dataRequest = this.taskController.Page(filter);

        var pageResult = dataRequest.pipe(
            map(res => {
                return {
                    agendaKey: agendaKey,
                    pageIndex: res.pageIndex,
                    pageSize: res.pageSize,
                    total: res.total,
                    results: res.results.map((t) => {
                        return t as Honeycomb.Tenant.Tasker.IService.Model.Task;
                    })
                } as TaskPageResult; // getting badly generated honeycomb-api.ts
            }),
            catchError(error => {
                console.error('task-service', error);
                
                let emptyResult = { agendaKey: agendaKey, pageIndex: 0, pageSize: this.pageSize(agendaKey), total: 0, results: [] } as TaskPageResult;
                return of(emptyResult);
            } )
          );
        
        pageResult.pipe(tap(pageResultData => { 
            console.log('PageResult', agendaKey, pageResultData.agendaKey, pageResultData.results.map(t => t.operation.taskType));
        }));

        //var pageResultData = await lastValueFrom(pageResult);

        return pageResult;
    }

    public async load(agendaKey?: string, useMutipleCalls?: boolean, callIndex?: number): Promise<void> {
        this.loadingData.emit({ agendaKey: agendaKey, loading: true });

        this.callIndex = callIndex;
        // if (this.loading.getValue() && !useMutipleCalls) {
        //     return;
        // };

        this.loading.next({ agendaKey: agendaKey, loading: true });

        this.taskSearchProperties.listFilter.pageIndex = this.taskSearchProperties.pageIndexMap.get(agendaKey) || 0;
        this.taskSearchProperties.listFilter.pageSize = this.taskSearchProperties.pageSizeMap.get(agendaKey) || 10;

        (await this.loadPage(agendaKey)).subscribe(async pageResult => { 

            let aKey = pageResult.agendaKey;

            if (pageResult.total === 0) {
                var newTotal = (this.taskSearchProperties.listFilter.pageSize * this.taskSearchProperties.listFilter.pageIndex) + pageResult.results.length;
                if (!isNullOrWhitespace(aKey) && pageResult.results.length === this.taskSearchProperties.pageSizeMap.get(aKey)) {
                    newTotal = ((this.taskSearchProperties.listFilter.pageSize * this.taskSearchProperties.listFilter.pageIndex) || 0) + 2 * this.taskSearchProperties.listFilter.pageSize;
                    this.total.next(newTotal);
                } else if (pageResult.results.length < this.taskSearchProperties.listFilter.pageSize) {
                    var newTotal = (this.taskSearchProperties.listFilter.pageSize * this.taskSearchProperties.listFilter.pageIndex) + pageResult.results.length;
                }
                pageResult.total = newTotal;
                this.totalStatic = newTotal;
            }

            // another call is in progress, just ignore previous result
            if (useMutipleCalls && this.callIndex > callIndex) {
                return;
            }

            this.totalStatic = pageResult.total;
            this.total.next(pageResult.total);

            this.taskSearchProperties.results = pageResult.results;

            this.data.emit({ agendaKey: pageResult.agendaKey, results: pageResult.results, total: pageResult.total, pageIndex: pageResult.pageIndex, pageSize: pageResult.pageSize });
            this.loading.next({ agendaKey: aKey, loading: false });

            this.loadingData.emit({ agendaKey: aKey, loading: false });
        });
    }


    public setPager = (agendaKey: string, pageIndex: number, pageSize?: number) => {

        console.log(`agendaKey: ${agendaKey}, pageIndex: ${pageIndex}, pageSize: ${pageSize}`);

        if (!isNullOrUndefined(pageSize)) {
            this.taskSearchProperties.pageSizeMap.set(agendaKey, pageSize);
        } else {
            pageSize = this.taskSearchProperties.pageSizeMap.get(agendaKey) || 10;
        }

        if (!isNullOrUndefined(pageIndex)) {
            this.taskSearchProperties.pageIndexMap.set(agendaKey, pageIndex);
        } else {
            pageIndex = this.taskSearchProperties.pageIndexMap.get(agendaKey) || 0;
        }
      
        if (!isNullOrUndefined(pageSize)) {
            this.taskSearchProperties.listFilter.offset = pageSize * pageIndex;
            this.taskSearchProperties.listFilter.limit = pageSize;
            this.taskSearchProperties.listFilter.pageSize = pageSize;
        }
    }

    public setSort(sortColumn: string, direction: string, isAttribute?: boolean) {
        console.log(`Sort by: ${sortColumn}`);
        this.taskSearchProperties.sort.column = sortColumn;
        this.taskSearchProperties.sort.direction = direction;
        this.taskSearchProperties.sort.isAttribute = isAttribute || false;
    }

    public getSort(): Honeycomb.Tenant.Tasker.IService.Model.TaskPageSort
    {
        return Object.assign({}, this.taskSearchProperties.sort);
    }

    public async setFilter(filter: Honeycomb.Tenant.Tasker.IService.TaskListFilter) {
        var lf = this.taskSearchProperties.listFilter;
        lf.taskRelationAdditional = filter.taskRelationAdditional;
        lf.userTaskRelations = filter.userTaskRelations;
        lf.includeAttributes = filter.includeAttributes;
        lf.mainFilter = filter.mainFilter;
        lf.jobID = filter.jobID;
        lf.locations = filter.locations;
        lf.locationID = filter.locationID;
        lf.taskSequenceID = filter.taskSequenceID;
        lf.baseTaskStatuses = filter.baseTaskStatuses;
        lf.taskRead = filter.taskRead;
        lf.taskStatusIDs = filter.taskStatusIDs;
        lf.taskTypes = filter.taskTypes;
        lf.dateFrom = filter.dateFrom;
        lf.dateTo = filter.dateTo;
        lf.priority = filter.priority;
        lf.priorities = filter.priorities;

        if (!!filter.pageIndex && !!filter.pageSize) {
            lf.pageIndex = filter.pageIndex;
            lf.pageSize = filter.pageSize;
        }
        if (!lf.pageIndex) {
            lf.pageIndex = 0;
        }

        if (!lf.pageSize) {
            lf.pageSize = 10;
        }

        lf.fulltext = filter.fulltext;
        lf.operations = filter.operations;
        lf.sortParams = filter.sortParams;
    }

    public getFilterRef(): Honeycomb.Tenant.Tasker.IService.TaskListFilter {
        return this.taskSearchProperties.listFilter;
    }

    public getFilterCopy(): Honeycomb.Tenant.Tasker.IService.TaskListFilter {
        var res =  Object.assign({}, this.taskSearchProperties.listFilter);
        return res;
    }
}